import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { mainApiUrl, storeId as storeIdConstant } from '@constants'
import { draftUpdater } from '@src/utils'
import { prepareHeaders } from '@src/reduxjs'

const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery: fetchBaseQuery({ baseUrl: mainApiUrl, prepareHeaders }),
  tagTypes: ['Orders'],
  endpoints: (builder) => ({
    get: builder.query({
      query: (data = {}) => {
        const params = {}

        if (data.search) {
          params.search = window.encodeURIComponent(data.search)
        }

        if (data.page) {
          params.page = data.page
        }

        if (data.paymentMethod) {
          params.payment_method = data.paymentMethod
        }

        if (data.invoiceStatus) {
          params.invoice_status = data.invoiceStatus
        }

        if (data.status) {
          params.status = data.status
        }

        if (data.perPage) {
          params.per_page = data.perPage
        }

        if (data.startAt) {
          params.start_at = data.startAt
        }

        if (data.locals && data.locals.length) {
          data.locals.forEach((local, index) => {
            params[`locals[${index}]`] = local.id
          })
        }

        return { url: `stores/${data.storeId || storeIdConstant}/orders`, params }
      },
      providesTags: (results) => {
        if (!results) {
          return [{ type: 'Orders', id: 'LIST' }]
        }
        return [...results.data.map((i) => ({ type: 'Orders', id: i.id })), { type: 'Orders', id: 'LIST' }]
      },
    }),
    getOne: builder.query({
      query: ({ order }) => {
        return {
          url: `orders/${order.id}`,
          method: 'GET',
        }
      },
      async onQueryStarted({ order, getQueryParams }, { dispatch, queryFulfilled }) {
        dispatch(ordersApi.util.updateQueryData('get', getQueryParams, draftUpdater({ ...order, isUpdating: true })))

        try {
          const { data } = await queryFulfilled
          if (data) {
            dispatch(ordersApi.util.updateQueryData('get', getQueryParams, draftUpdater(data.data)))
          }
        } catch {
          dispatch(ordersApi.util.invalidateTags(['Orders']))
        }
      },
    }),
    getAutocomplete: builder.mutation({
      query: ({ q, limit, language }) => {
        return {
          url: `/orders/address/autocomplete`,
          method: 'GET',
          params: { q, limit, language },
        }
      },
    }),
    getLookup: builder.mutation({
      query: ({ id }) => {
        return {
          url: `/orders/address/lookup`,
          method: 'GET',
          params: { id },
        }
      },
    }),
    updateOrder: builder.mutation({
      query: (data) => {
        return {
          url: `orders/${data.id}`,
          method: 'PATCH',
          body: data,
        }
      },
    }),
    getRedsysForm: builder.mutation({
      query: (order) => {
        return {
          url: `orders/${order.id}/redsys`,
          method: 'GET',
        }
      },
    }),
    getPixCharge: builder.mutation({
      query: (order) => {
        return {
          url: `orders/${order.id}/pix`,
          method: 'GET',
        }
      },
    }),
    dispatchOrder: builder.mutation({
      query: ({ id, method }) => {
        return {
          url: `orders/${id}/dispatch`,
          method: 'POST',
          body: { method },
        }
      },
    }),
    createInvoice: builder.mutation({
      query: (data) => {
        return {
          url: `orders/${data.id}/invoice`,
          method: 'POST',
          body: data,
        }
      },
    }),
    delete: builder.mutation({
      query: ({ id }) => ({ url: `orders/${id}`, method: 'DELETE' }),
      async onQueryStarted({ id, getQueryParams }, { dispatch, queryFulfilled }) {
        dispatch(ordersApi.util.updateQueryData('get', getQueryParams, (draft) => {
          draft.data = draft.data.filter(i => i.id !== id)
        }))

        try {
          await queryFulfilled
        } catch {
          dispatch(ordersApi.util.invalidateTags(['Orders']))
        }
      },
    }),
  }),
})

export const { useGetOneQuery, useGetQuery, useGetAutocompleteMutation, useGetLookupMutation, useUpdateOrderMutation, useDispatchOrderMutation, useCreateInvoiceMutation, useGetRedsysFormMutation, useGetPixChargeMutation, useDeleteMutation } = ordersApi

export default ordersApi
